import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { FaEye, FaEyeSlash, FaUserPlus, FaSignInAlt } from 'react-icons/fa';
import '../styles/auth.css';

const Login = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, signup } = useAuth();
  
  const [isRegistering, setIsRegistering] = useState(location.pathname === '/signup');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Redirect path after authenticating
  const from = location.state?.from?.pathname || '/';

  const validateForm = () => {
    const tempErrors = {};
    if (!email) {
      tempErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      tempErrors.email = 'Invalid email address';
    }
    if (!password) {
      tempErrors.password = 'Password is required';
    } else if (password.length < 6) {
      tempErrors.password = 'Password must be at least 6 characters';
    }
    if (isRegistering && password !== confirmPassword) {
      tempErrors.confirmPassword = 'Passwords do not match';
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const modeText = isRegistering ? 'Creating account...' : 'Signing in...';
    const toastId = toast.loading(modeText);

    try {
      if (isRegistering) {
        // Sign Up Flow
        await signup(email, password);
        toast.success('Registration successful! Please check your email for a verification link.', { id: toastId, duration: 6000 });
        setIsRegistering(false);
        setPassword('');
        setConfirmPassword('');
      } else {
        // Sign In Flow
        await login(email, password);
        toast.success('Login successful!', { id: toastId });
        
        // Redirect: Admin goes to admin-dashboard, others to 'from' page
        if (email.toLowerCase() === 'gramture@gmail.com') {
          navigate('/admin-dashboard');
        } else {
          navigate(from, { replace: true });
        }
      }
    } catch (err) {
      console.error('Authentication error:', err);
      toast.error(err.message || 'Authentication failed. Please check details.', { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const toggleAuthMode = () => {
    setIsRegistering(!isRegistering);
    setErrors({});
    setPassword('');
    setConfirmPassword('');
  };

  return (
    <div className="auth-page-wrapper">
      <div className="auth-panel glass-panel">
        <div className="auth-header">
          <h2>{isRegistering ? 'Create Account' : 'Welcome Back'}</h2>
          <p>{isRegistering ? 'Register to start your educational journey' : 'Sign in to access your dashboard'}</p>
        </div>

        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              placeholder="e.g. name@example.com"
              className="glass-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
            />
            {errors.email && <span className="validation-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                placeholder={isRegistering ? 'Min. 6 characters' : 'Enter your password'}
                className="glass-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
              />
              <button
                type="button"
                className="password-toggle-btn"
                onClick={() => setShowPassword(!showPassword)}
                disabled={loading}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </button>
            </div>
            {errors.password && <span className="validation-error">{errors.password}</span>}
          </div>

          {isRegistering && (
            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <input
                type="password"
                id="confirmPassword"
                placeholder="Re-enter password"
                className="glass-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
              />
              {errors.confirmPassword && (
                <span className="validation-error">{errors.confirmPassword}</span>
              )}
            </div>
          )}

          <button
            type="submit"
            className="btn-premium btn-premium-primary"
            style={{ width: '100%', marginTop: '1rem', padding: '12px' }}
            disabled={loading}
          >
            {loading ? (
              'Processing...'
            ) : isRegistering ? (
              <>
                <FaUserPlus /> Register Account
              </>
            ) : (
              <>
                <FaSignInAlt /> Sign In
              </>
            )}
          </button>
        </form>

        <div className="auth-footer-actions">
          {isRegistering ? 'Already have an account?' : "Don't have an account?"}
          <button 
            type="button" 
            onClick={toggleAuthMode}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--primary-color)',
              fontWeight: '700',
              cursor: 'pointer',
              marginLeft: '6px',
              textDecoration: 'underline'
            }}
          >
            {isRegistering ? 'Sign In' : 'Register Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
